import i18n, { renderMoney, translateEnum } from '@rygen/rygen-ui/i18n';
import {
  ActionRecord,
  CarrierRef,
  Currency,
  Optional,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { LoadboardSource } from '../loadboard/LoadboardSource';

import { CorsairActionType } from './CorsairActionType';

/**
 * The type that we are going to assume `ActionRecord.context` has
 *
 * Since we have to format the context object for use with `vue-i18`, new
 * `actionType`s should make sure their context fits this type. _Note_: This
 * only matters for non-primitive values that `vue-i18n` can not interpolate
 * directly or primitive types that require other values for rendering, e.g.
 * money amount and currency code. All other values can be omitted from this
 * type and used directly in translations.
 *
 * The first block is what we expect to be passed from the backend. When
 * present, those keys will be formatted in a standard way (depending on the
 * intent of the key).
 *
 * The second block is the set of keys that we use for the formatted output.
 * These are the keys that will be used within translations. Backend handlers
 * should avoid using these keys to prevent collisions.
 */
type Context = {
  /** Corresponds to `recipients` and `count` */
  emails: Optional<string[]>;
  /** Corresponds to `carrierName` */
  carrier: Optional<CarrierRef>;
  /** Corresponds to `brokerName` */
  broker: Optional<CarrierRef>;
  /** Corresponds to `rate`, also requires `currencyCode` */
  totalPrice: Optional<number>;
  /** Corresponds to `rate`, also requires `totalPrice` */
  currencyCode: Optional<Currency>;
  /** Corresponds to `fmtTenderMethod` */
  tenderMethod: Optional<TenderMethod>;
  /** Corresponds to `fmtResponseMethod` */
  responseMethod: Optional<TenderMethod>;
  /** Usernames of users assigned to an order or shipment */
  assignedUsers: Optional<string[]>;
  /** Loadboards that a user has posted an order or shipment to */
  loadboards: Optional<LoadboardSource[]>;

  [key: string]: unknown;

  /**
   * A comma-separate list of email addresses
   *
   * Depends on `emails`
   */
  recipients: Optional<string>;
  /**
   * The number of email addresses or loadboards
   *
   * Depends on `emails` or `loadboards`
   */
  count: Optional<number>;
  /**
   * Usernames of assigned users formatted as a string
   *
   * Depends on `assignedUsers`
   */
  usernames: Optional<string>;
  /**
   * The carrier name and scac
   *
   * Depends on `carrier`
   */
  carrierName: Optional<string>;
  /**
   * The broker name and scac
   *
   * Depends on `broker`
   */
  brokerName: Optional<string>;
  /**
   * A monetary amount formatted with currency
   *
   * Depends on `totalPrice` and `currency`
   */
  rate: Optional<string>;
  /**
   * Formatted tender method
   *
   * Depends on `tenderMethod`
   */
  fmtTenderMethod: Optional<string>;
  /**
   * A description of the tender config options
   *
   * Depends on `tenderMethod`. Defaults to an empty string for ease of use
   * in locale file.
   */
  tenderMethodExplainer: string;
  /**
   * A description of the response method

   * Depends on `respnoseMethod`. Defaults to an empty string for ease of use
   * in locale file.
   */
  fmtResponseMethod: string;
  /**
   * A comma separated list of loadboards this entity has successfully been posted to.
   */
  loadboardNames: Optional<string>;
  /**
   * The translated entity name
   *
   * Depends on this.entityClass
   */
  entity: String;
};

export class CorsairActionRecord extends ActionRecord {
  declare actionType?: CorsairActionType;
  declare context: Context;
  declare entityClass: string;

  constructor(opts?: Partial<CorsairActionRecord>) {
    super(opts);
    this.formatContext();
  }

  /**
   * Format `context` for output in the audit log
   *
   * `context` may have structured data that vue-i18n can't interpolate directly
   * so this method formats the data into strings for output.
   */
  formatContext() {
    const emails = this.context.emails;
    if (emails !== undefined) {
      this.context.recipients = emails.join(', ');
      this.context.count = emails.length;
    }

    const usernames = this.context.assignedUsers;
    if (usernames !== undefined) {
      this.context.usernames = usernames.join(', ');
    }

    const carrier = this.context.carrier;
    if (carrier !== undefined) {
      this.context.carrierName = `${carrier.name} (${carrier.scac})`;
    }

    const broker = this.context.broker;
    if (broker !== undefined) {
      this.context.brokerName = `${broker.name} (${broker.scac})`;
    }

    const totalPrice = this.context.totalPrice;
    const currencyCode = this.context.currencyCode;
    if (totalPrice !== undefined && currencyCode) {
      this.context.rate = renderMoney(totalPrice, { currencyCode });
    }

    const tenderMethod = this.context.tenderMethod;
    if (tenderMethod) {
      this.context.fmtTenderMethod = translateEnum(
        tenderMethod,
        'TenderMethod',
      );
      this.context.tenderMethodExplainer =
        tenderMethod === TenderMethod.EMAIL
          ? i18n.global.t('shipments.tenderWithEmails', {
              recipients: this.context.recipients,
            })
          : '';
    }

    const responseMethod = this.context.responseMethod;
    this.context.fmtResponseMethod = responseMethod
      ? i18n.global.t('shipments.tenderViaResponseMethod', { responseMethod })
      : '';

    const loadboards = this.context.loadboards;
    if (loadboards !== undefined) {
      this.context.loadboardNames = loadboards
        .map((l) => translateEnum(l, 'LoadboardSource'))
        .join(', ');
      this.context.count = loadboards.length;
    }

    this.context.entity =
      this.entityClass === 'Order'
        ? i18n.global.t('orders.orders')
        : i18n.global.t('shipments.shipments');
  }
}
